import {
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Radio,
  RadioGroup,
  Select,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Currency,
  PublicBidStatus,
  SourcingEventV2Dto,
  useAwardSourcingEventMutation,
  useListPublicBidsForEventQuery,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { useDisplayDate } from "../../../../common/formatting/useDisplayDate";

export const SelectWinner = ({ event }: { event: SourcingEventV2Dto }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const apiError = useApiError();
  const displayDate = useDisplayDate();
  const { data, isLoading, isError } = useListPublicBidsForEventQuery({ eventId: event.id });
  const [award, { isLoading: isAwarding }] = useAwardSourcingEventMutation();

  const bids = (data?.bids ?? []).filter((b) => b.status === PublicBidStatus.Submitted);

  const [winnerId, setWinnerId] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [currency, setCurrency] = useState<Currency>(event.publicContext.estimatedValue?.currency ?? Currency.Nok);
  const [amount, setAmount] = useState(
    event.publicContext.estimatedValue?.value != null ? String(event.publicContext.estimatedValue.value) : "",
  );

  if (isLoading) {
    return (
      <Center py="16">
        <Spinner color="smPrimary" />
      </Center>
    );
  }
  if (isError) {
    return (
      <Center py="16">
        <Text color="smError">{t("Could not load bids.")}</Text>
      </Center>
    );
  }
  if (bids.length === 0) {
    return (
      <Center py="16">
        <Text color="smMuted">{t("No delivered bids to award yet.")}</Text>
      </Center>
    );
  }

  const submit = async () => {
    if (!winnerId) return;
    const parsed = amount.trim() === "" ? null : Number(amount);
    try {
      await award({
        eventId: event.id,
        awardSourcingEventRequest: {
          winningBidId: winnerId,
          internalNote: internalNote.trim() || null,
          eventValue: parsed != null && !Number.isNaN(parsed) ? { value: parsed, currency } : null,
        },
      }).unwrap();
      toast({ title: t("Event awarded"), status: "success", position: "top-right" });
    } catch (e) {
      apiError.trigger(e instanceof Error ? e : new Error("Unknown error"));
    }
  };

  return (
    <Stack spacing="6">
      <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" overflow="hidden">
        <TableContainer>
          <RadioGroup value={winnerId} onChange={setWinnerId}>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>{t("Winner")}</Th>
                  <Th>{t("Supplier")}</Th>
                  <Th>{t("Submitted")}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {bids.map((bid) => (
                  <Tr key={bid.id}>
                    <Td>
                      <Radio value={bid.id} colorScheme="teal" />
                    </Td>
                    <Td fontWeight="medium" color="smText">
                      {bid.ownerName || t("Unknown")}
                    </Td>
                    <Td color="smMuted">{bid.submittedAt ? displayDate(bid.submittedAt) : "–"}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </RadioGroup>
        </TableContainer>
      </Box>

      <FormControl>
        <FormLabel>{t("Estimated contract value")}</FormLabel>
        <HStack>
          <Input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            bg="smBackground"
            maxW="xs"
            placeholder="0"
          />
          <Select value={currency} onChange={(e) => setCurrency(e.target.value as Currency)} bg="smBackground" maxW="32">
            {Object.values(Currency).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </HStack>
      </FormControl>

      <FormControl>
        <FormLabel>{t("Internal note")}</FormLabel>
        <Textarea value={internalNote} onChange={(e) => setInternalNote(e.target.value)} bg="smBackground" rows={2} />
        <FormHelperText>{t("Only visible to your organization.")}</FormHelperText>
      </FormControl>

      <Flex justify="end" align="center" gap="3">
        <Text fontSize="sm" color="smMuted">
          {t("You'll prepare and send award letters to each supplier after awarding.")}
        </Text>
        <Button colorScheme="teal" onClick={submit} isLoading={isAwarding} isDisabled={!winnerId}>
          {t("Award to selected supplier")}
        </Button>
      </Flex>
    </Stack>
  );
};
